import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { graphql } from 'gatsby'
import {
  Button,
  Container,
  Section,
  SectionTitle,
  ListItem,
} from 'chaoskit/src/components'
import { Fragment } from 'react'
import { text } from 'chaoskit/src/assets/styles/utility'

import {
  HomeHero,
  HomeHeroGrid,
  HomeHeroGridImage,
  HomeHeroGridContent,
} from '~partials/home/Hero'
import { ProductGridBlock, ProductGridBlockContent } from '~partials/product/UI'
import BaseLayout from '~layouts/BaseLayout'
import { HeroContent, HeroTitle, HeroTitleSub } from '~partials/Hero'
import Data from '~components/Data'
import CustomerLogos from '~partials/global/CustomerLogos'
import { ReactComponent as HeroSVG } from '~media/aws/hero.svg'
import { ReactComponent as WebinarSvg } from '~media/aws/webinar.svg'
import HelmetSEO from '~partials/global/HelmetSEO'
import Image from '~components/Image'
import FilteredContent from '~components/FilteredContent'
import Quote from '~components/Quote'
import ButtonLink from '~components/ButtonLink'
import PlusDecorationList from '~components/PlusDecorationList'
import TextLink from '~components/TextLink'
import { ReactComponent as Ec2Svg } from '~media/aws/ec2.svg'
import { ReactComponent as K8sSvg } from '~media/aws/k8s.svg'
import { ReactComponent as ContainerServicesSvg } from '~media/aws/container-services.svg'
import { ReactComponent as LambdaSvg } from '~media/aws/lambda.svg'
import { ReactComponent as S3Svg } from '~media/aws/s3.svg'
import { ReactComponent as RdsSvg } from '~media/aws/rds.svg'
import { ReactComponent as DynamodbSvg } from '~media/aws/dynamodb.svg'
import { ReactComponent as ElasticacheSvg } from '~media/aws/elasticache.svg'
import { ReactComponent as Route53Svg } from '~media/aws/route-53.svg'
import { ReactComponent as DirectConnectSvg } from '~media/aws/direct-connect.svg'
import { ReactComponent as AppMeshSvg } from '~media/aws/app-mesh.svg'
import { ReactComponent as CloudfrontSvg } from '~media/aws/cloudfront.svg'
import { ReactComponent as CloudwatchSvg } from '~media/aws/cloudwatch.svg'
import { ReactComponent as XraySvg } from '~media/aws/x-ray.svg'
import { ReactComponent as EventbridgeSvg } from '~media/aws/eventbridge.svg'
import { ReactComponent as SnsSvg } from '~media/aws/sns.svg'
import AWSQuoteGraphic from '~components/AWSQuoteGraphic'

const AWSHero = ({ data }) => {
  const theme = useTheme()

  return (
    <HomeHero css={{ paddingBottom: 0 }}>
      <HeroContent>
        <HomeHeroGrid>
          <HomeHeroGridImage>
            <HeroSVG />
          </HomeHeroGridImage>
          <HomeHeroGridContent>
            <HeroTitle size="large">
              <Data>{data.heading}</Data>
            </HeroTitle>
            <HeroTitleSub>
              <Data>{data.blurb}</Data>
            </HeroTitleSub>
            <Button
              url={data.marketplace.url}
              noContrast
              type="primary"
              css={{ marginTop: theme.space.large }}
            >
              <Data>{data.marketplace.label}</Data>
            </Button>
          </HomeHeroGridContent>
        </HomeHeroGrid>
        <CustomerLogos
          css={{ paddingBottom: `${theme.space.base}px !important` }}
          contrast
        />
        <Container
          css={{
            marginTop: theme.space.xlarge,
            display: 'grid',
            gap: theme.space.large,
            alignItems: 'center',

            [theme.mq.medium]: {
              gridTemplateColumns: 'repeat(2, 1fr)',
            },
          }}
        >
          <AWSQuoteGraphic
            css={{
              [theme.mq.medium]: {
                marginBottom: -theme.space.xlarge,
              },
            }}
          />
          <div css={{ paddingBottom: theme.space.large, position: 'relative' }}>
            <Quote
              contrastQuotes
              author={<Data>{data.quote.author}</Data>}
              size="large"
              css={{
                '.G__Quote__Author': {
                  ...theme.text.small,
                  ...text.expanded(theme),
                },
              }}
            >
              <Data>{data.quote.blurb}</Data>
            </Quote>
            <TextLink
              href={data.quote.link}
              css={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1,
              }}
            />
          </div>
        </Container>
      </HeroContent>
    </HomeHero>
  )
}

AWSHero.propTypes = {
  data: PropTypes.object.isRequired,
}

const AWSWebinar = ({ data }) => {
  const theme = useTheme()

  return (
    <Section>
      <Container>
        <ProductGridBlock>
          <WebinarSvg
            css={{
              width: '100%',
              maxWidth: 550,
              margin: '0 auto',

              [theme.mq.medium]: {
                gridColumn: '6 / span 7',
                gridRow: 1,
                width: 700,
                maxWidth: 'none',
              },
            }}
          />
          <ProductGridBlockContent
            css={{
              [theme.mq.medium]: {
                gridColumn: '1 / span 6',
              },
            }}
          >
            <SectionTitle
              align={{ medium: 'left' }}
              title={<Data>{data.heading}</Data>}
            />
            <div
              css={{
                [theme.mq.small__max]: {
                  textAlign: 'center',
                },
              }}
            >
              <ButtonLink type="default" url={data.cta.url}>
                {data.cta.label}
              </ButtonLink>
            </div>
          </ProductGridBlockContent>
        </ProductGridBlock>
      </Container>
    </Section>
  )
}

AWSWebinar.propTypes = {
  data: PropTypes.object.isRequired,
}

const AWSUseCaseImage = ({ as: Component, ...rest }) => {
  const theme = useTheme()

  return <Component css={{ width: theme.height.large }} {...rest} />
}

AWSUseCaseImage.propTypes = {
  as: PropTypes.any.isRequired,
}

const AWSUseCase = ({ data }) => {
  const theme = useTheme()

  return (
    <div
      css={{
        display: 'grid',
        gap: theme.space.large,

        [theme.mq.medium]: {
          gap: theme.space.base,
          gridTemplateColumns: 'auto 1fr',
        },
      }}
    >
      <div
        css={{
          display: 'grid',
          gap: theme.space.base,
          alignContent: 'start',

          [theme.mq.small__max]: {
            gridTemplateColumns: 'repeat(4, auto)',
            justifyContent: 'center',
          },
        }}
      >
        {data.icons === 'compute' && (
          <Fragment>
            <AWSUseCaseImage as={Ec2Svg} alt="EC2" />
            <AWSUseCaseImage as={K8sSvg} alt="Kubernetes" />
            <AWSUseCaseImage
              as={ContainerServicesSvg}
              alt="Container Service"
            />
            <AWSUseCaseImage as={LambdaSvg} alt="Lambda" />
          </Fragment>
        )}

        {data.icons === 'storage' && (
          <Fragment>
            <AWSUseCaseImage as={S3Svg} alt="S3" />
            <AWSUseCaseImage as={RdsSvg} alt="RDS" />
            <AWSUseCaseImage as={DynamodbSvg} alt="DyanmoDB" />
            <AWSUseCaseImage as={ElasticacheSvg} alt="ElastiCache" />
          </Fragment>
        )}

        {data.icons === 'networking' && (
          <Fragment>
            <AWSUseCaseImage as={Route53Svg} alt="Route 53" />
            <AWSUseCaseImage as={DirectConnectSvg} alt="Direct Connect" />
            <AWSUseCaseImage as={AppMeshSvg} alt="App Mesh" />
            <AWSUseCaseImage as={CloudfrontSvg} alt="CloudFront" />
          </Fragment>
        )}

        {data.icons === 'management' && (
          <Fragment>
            <AWSUseCaseImage as={CloudwatchSvg} alt="CloudWatch" />
            <AWSUseCaseImage as={XraySvg} alt="X-Ray" />
            <AWSUseCaseImage as={EventbridgeSvg} alt="Event Bridge" />
            <AWSUseCaseImage as={SnsSvg} alt="SNS" />
          </Fragment>
        )}
      </div>
      <div>
        <h3
          css={{
            [theme.mq.medium]: {
              paddingLeft: theme.space.large,
            },
          }}
        >
          <Data>{data.heading}</Data>
        </h3>
        <PlusDecorationList>
          {data.items.map((item) => (
            <ListItem key={item.blurb}>
              <Data>{item.blurb}</Data>
            </ListItem>
          ))}
        </PlusDecorationList>
      </div>
    </div>
  )
}

AWSUseCase.propTypes = {
  data: PropTypes.object.isRequired,
}

const AWSUseCases = ({ data }) => {
  const theme = useTheme()

  return (
    <Section css={{ paddingBottom: '0 !important' }}>
      <Container>
        <SectionTitle
          as="h2"
          title={<Data>{data.heading}</Data>}
          sub={<Data>{data.blurb}</Data>}
        />
        <div
          css={{
            display: 'grid',
            gap: theme.space.large,

            [theme.mq.medium]: {
              columnGap: theme.space.xlarge,
              rowGap: theme.space.xlarge * 1.5,
              gridTemplateColumns: 'repeat(2, 1fr)',
            },
          }}
        >
          {data.items.map((usecase) => (
            <AWSUseCase key={usecase.icons} data={usecase} />
          ))}
        </div>
      </Container>
    </Section>
  )
}

AWSUseCases.propTypes = {
  data: PropTypes.object.isRequired,
}

const AWS = ({
  data: {
    file: {
      childMdx: { frontmatter: pageData },
    },
  },
}) => {
  const theme = useTheme()

  return (
    <BaseLayout footerProps={{ hideCTA: true }}>
      <HelmetSEO
        title={pageData.title}
        description={pageData.description}
        image={pageData.socialMediaImage}
      />
      <AWSHero data={pageData.hero} />
      <AWSWebinar data={pageData.awsWebinar} />
      <AWSUseCases data={pageData.awsUseCases} />
      <FilteredContent
        heading={pageData.awsDiscover.heading}
        filters="topics:AWS"
        hideSeparator
        css={{ paddingBottom: `${theme.space.xlarge}px !important` }}
      />
      <Section css={{ textAlign: 'center', paddingTop: '0 !important' }}>
        <Container>
          <Image
            css={{ maxWidth: 200 }}
            src="https://res.cloudinary.com/gremlin/image/upload/v1598050979/Landing%20Pages/DevOpsCompetency_MarketplaceSeller_AmazonLinux2_1.svg"
            alt="AWS Advanced Technology Partner"
          />
        </Container>
      </Section>
    </BaseLayout>
  )
}

AWS.propTypes = {
  data: PropTypes.object.isRequired,
}

export default AWS

export const pageQuery = graphql`
  query AWSPageData {
    file(name: { eq: "aws" }, absolutePath: { regex: "/src/data/" }) {
      childMdx {
        frontmatter {
          title
          description
          socialMediaImage
          hero {
            blurb
            heading
            marketplace {
              label
              url
            }
            quote {
              link
              blurb
              author
            }
          }
          awsWebinar {
            heading
            cta {
              label
              url
            }
          }
          awsUseCases {
            heading
            blurb
            items {
              heading
              icons
              items {
                blurb
              }
            }
          }
          awsDiscover {
            heading
          }
        }
      }
    }
  }
`
